import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema } from './common.js';
import { TASK_STATUSES } from '../enums.js';
/**
 * OAuth scopes granted to an MCP client (an AI coding agent). Deliberately coarse — a consent
 * screen nobody reads is worse than three lines somebody does — but split along the axis that
 * actually matters to a user deciding whether to click Authorize: can it only look, can it move
 * my tasks, can it publish links under my name.
 *
 * Note what is NOT here and cannot be granted at all: writing PRD/TDD documents, generating
 * tasks, assigning work to other people, touching org or project membership, or reading the
 * audit log. Those stay human-only by construction — there is no MCP tool that performs them,
 * so no scope could enable one.
 */
export const MCP_SCOPES = [
    'context:read',
    'tasks:read',
    'tasks:write',
    'deliverables:write',
];
export const McpScopeSchema = z.enum(MCP_SCOPES);
/** Shown verbatim on the consent screen, so phrase each one as a capability the user is handing
 * over rather than as an internal permission name. */
export const MCP_SCOPE_DESCRIPTION = {
    'context:read': 'Read project documents — the accepted PRD, TDD, your team’s conventions and the project’s deliverables.',
    'tasks:read': 'Read tasks, their acceptance criteria, comments and blockers.',
    'tasks:write': 'Move tasks it is working on to In progress, Testing or Blocked, post progress notes, and claim unassigned tasks. It can never mark work Done or In review.',
    'deliverables:write': 'Attach deliverables — links, deployments and screenshots — to a project.',
};
/**
 * The only statuses an agent may ever write. `done` and `in_review` are absent on purpose: the
 * whole point of the connector is that an agent hands finished work to a human, who tests it and
 * decides. `canceled` and `backlog` are absent because triage is a planning decision, not an
 * implementation one.
 *
 * Enforced in two places (apps/api/src/mcp/tools) — once when building each tool's input schema,
 * and again in the handler before the write — so a client that ignores the advertised JSON
 * schema still cannot get past the second check.
 */
export const MCP_WRITABLE_TASK_STATUSES = [
    'in_progress',
    'testing',
    'blocked',
];
export const McpWritableTaskStatusSchema = z.enum(MCP_WRITABLE_TASK_STATUSES);
/** Guards against a future edit to TASK_STATUSES silently orphaning the list above. */
const _writableAreRealStatuses = MCP_WRITABLE_TASK_STATUSES;
void _writableAreRealStatuses;
void TASK_STATUSES;
/**
 * oauthClients/{clientId} — a client registered through RFC 7591 dynamic client registration.
 *
 * Registration is open (the MCP spec expects it: a user types one `claude mcp add` and the
 * client registers itself), so a row here proves nothing about who created it. `clientName` is
 * attacker-controlled text and the consent screen must present it as such — see
 * apps/web/src/features/mcp/AuthorizeAppPage.tsx, which shows the redirect URI alongside the
 * name because the URI is the part an attacker cannot fake.
 */
export const OAuthClientSchema = z.object({
    clientId: IdSchema,
    clientName: z.string().max(200),
    redirectUris: z.array(z.string().url()).min(1),
    grantTypes: z.array(z.string()).default(['authorization_code', 'refresh_token']),
    responseTypes: z.array(z.string()).default(['code']),
    /** Public clients only — Claude Code is a native app and cannot keep a secret. PKCE is the
     * proof-of-possession instead, and it is mandatory (see apps/api/src/oauth/authorize.ts). */
    tokenEndpointAuthMethod: z.literal('none'),
    scope: z.string().optional(),
    softwareId: z.string().max(200).optional(),
    softwareVersion: z.string().max(100).optional(),
    createdAt: IsoDateTimeSchema,
    createdFromIp: z.string().optional(),
});
/**
 * oauthGrants/{grantId} — one user's standing authorization of one client. Access and refresh
 * tokens both point at a grant, so revoking the grant kills every token minted under it in a
 * single write, including ones issued minutes earlier. This is what the "Connected apps" card in
 * Settings deletes.
 */
export const OAuthGrantSchema = z.object({
    id: IdSchema,
    uid: IdSchema,
    clientId: IdSchema,
    clientName: z.string().max(200),
    scopes: z.array(McpScopeSchema),
    createdAt: IsoDateTimeSchema,
    lastUsedAt: IsoDateTimeSchema.nullable().default(null),
    revokedAt: IsoDateTimeSchema.nullable().default(null),
});
/** What GET /api/oauth/grants returns for the Settings card. Never includes token material. */
export const ConnectedAppSchema = OAuthGrantSchema.pick({
    id: true,
    clientName: true,
    scopes: true,
    createdAt: true,
    lastUsedAt: true,
});
/** What the consent screen renders — the pending authorization request, resolved for display. */
export const PendingAuthorizationSchema = z.object({
    requestId: IdSchema,
    clientName: z.string().max(200),
    /** Shown to the user because it is the one part of a self-registered client's identity that
     * an attacker cannot forge: the code goes here, so this is where the access actually lands. */
    redirectUri: z.string().url(),
    scopes: z.array(McpScopeSchema),
    expiresAt: IsoDateTimeSchema,
    /** True when this user has authorized this same client before — lets the screen say
     * "reconnecting" instead of implying a brand-new grant. */
    isReauthorization: z.boolean().default(false),
});
//# sourceMappingURL=mcp.js.map